import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react'
import {
    Link,
    useLocation,
    useNavigate,
    useNavigationType,
} from 'react-router-dom'
import {
    CollapseToggle,
    DashboardLayout,
} from '@vnetwork/datacentral-ui/shells'
import { Text } from '@vnetwork/datacentral-ui/primitives'
import ChristmasGreeting from '@/components/ChristmasGreeting'
import { HeaderActions, HeaderStart, SidebarSearch } from '@/app/topbar'
import { PageTabBarSlot } from '@/components/Headers/SubHeaders'
import MobileTabBar from '@/components/Sidebar/MobileTabBar'
import Logo from '@/components/Sidebar/Head/Logo'
import { useSidebarNav } from '@/components/Sidebar/useSidebarNav'
import TetGreeting from '@/components/TetGreeting'
import SidePanel from '@/app/side-panel'
import { useAppStore } from '@/stores/useAppStore'
import { getAppScroller } from '@/utils/scroll'

// The help panel (markdown renderer) loads the first time help opens.
const HelpPanel = lazy(() => import('@/components/HelpPanel'))

function Brand({ collapsed }: { collapsed: boolean }) {
    return (
        <Link to="/" className="flex items-center gap-2 no-underline min-w-0">
            <Logo className="w-6 h-6 shrink-0" />
            {!collapsed && (
                // Below sm the logo alone: the topbar action cluster (with the
                // language switch) needs the width.
                <Text
                    weight="bold"
                    size="xl"
                    tone="primary"
                    truncate
                    className="hidden sm:block"
                >
                    DataCentral
                </Text>
            )}
        </Link>
    )
}

const MainLayout = ({ children }: { children: React.ReactNode }) => {
    const {
        sideBarFull,
        setSideBarFull,
        isShowHelp,
        helpContent,
        helpTitle,
        hideHelp,
        sidePanel,
    } = useAppStore((state) => state)
    const { nav, activeId } = useSidebarNav()
    const navigate = useNavigate()
    const location = useLocation()
    const navigationType = useNavigationType()

    // The shell scrolls <main>, not the window, so the browser can't restore
    // it: remember each history entry's offset and behave like the browser —
    // a new page (PUSH) starts at the top, Back/Forward (POP) restores.
    const scrollPositions = useRef(new Map<string, number>())
    const activeKey = useRef(location.key)
    const prevPathname = useRef(location.pathname)

    // Before the new page paints, so offsets clamped by its render are filed
    // under the new entry, not the one being left.
    useLayoutEffect(() => {
        activeKey.current = location.key
    }, [location.key])

    useEffect(() => {
        const scroller = getAppScroller()
        if (!scroller) return
        let frame = 0
        const onScroll = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                scrollPositions.current.set(
                    activeKey.current,
                    scroller.scrollTop
                )
            })
        }
        scroller.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            cancelAnimationFrame(frame)
            scroller.removeEventListener('scroll', onScroll)
        }
    }, [])

    useEffect(() => {
        const scroller = getAppScroller()
        const pathChanged = prevPathname.current !== location.pathname
        prevPathname.current = location.pathname
        if (!scroller) return
        if (navigationType === 'POP') {
            // No saved offset (entry never scrolled, or restored after a reload)
            // → start at the top, like a fresh visit; same-path POPs (query-only
            // changes) keep their place, mirroring the PUSH rule below.
            const top =
                scrollPositions.current.get(location.key) ??
                (pathChanged ? 0 : undefined)
            if (top !== undefined) {
                const frame = requestAnimationFrame(() =>
                    scroller.scrollTo({ top })
                )
                return () => cancelAnimationFrame(frame)
            }
            return
        }
        if (navigationType === 'PUSH' && pathChanged) {
            scroller.scrollTo({ top: 0 })
        }
    }, [location.key, location.pathname, navigationType])

    return (
        <>
            <DashboardLayout
                brand={<Brand collapsed={!sideBarFull} />}
                topbarStart={<HeaderStart />}
                topbarActions={<HeaderActions />}
                // Tables offset their sticky headers by this element's height.
                topbarProps={{ id: 'main-header' }}
                nav={nav}
                activeId={activeId}
                // The global search sits at the top of the sidebar; the topbar
                // carries the page trail.
                search={<SidebarSearch />}
                // Pages with a tab bar portal it here, above the content.
                notice={<PageTabBarSlot />}
                onNavigate={(item) => {
                    if (item.href && !item.external) navigate(item.href)
                }}
                collapsed={!sideBarFull}
                onCollapsedChange={(collapsed) => setSideBarFull(!collapsed)}
                sidebarFooter={
                    <div className="flex w-full items-center gap-1">
                        <CollapseToggle />
                    </div>
                }
                mobileNav={<MobileTabBar nav={nav} activeId={activeId} />}
                panel={
                    isShowHelp ? (
                        <Suspense fallback={null}>
                            <HelpPanel
                                content={helpContent}
                                title={helpTitle}
                                onClose={hideHelp}
                            />
                        </Suspense>
                    ) : sidePanel ? (
                        <SidePanel />
                    ) : null
                }
            >
                {children}
            </DashboardLayout>

            {/* Event greetings - each shows once per event period */}
            <ChristmasGreeting />
            <TetGreeting />
        </>
    )
}

export default MainLayout
